import { ButtonHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useCountUp } from "../../hooks/useCountUp";
import "./ritiri.css";

/**
 * Testata delle pagine Ritiri valori (docs/specs/ritiri-valori-riferimento-grafico.md):
 * fascia primary a tutta larghezza con alone, sopratitolo, titolo, sottotitolo,
 * pillola di stato e riquadri KPI a destra. Serve all'import e alla
 * dashboard.
 *
 * "A tutta larghezza" rispetto all'area del contenuto: i margini negativi
 * annullano il padding del contenitore di `AppLayout`, così il layout comune
 * dell'app non cambia.
 */

export interface KpiRitiri {
  etichetta: string;
  valore: number | null;
  /** Riquadro "caldo": errori o scarti da guardare. */
  caldo?: boolean;
}

export interface PillolaRitiri {
  testo: ReactNode;
  allarme?: boolean;
}

interface Props {
  sopratitolo: string;
  titolo: string;
  sottotitolo?: ReactNode;
  /** Una pillola di stato, o più d'una (la dashboard ne mostra due). */
  pillola?: PillolaRitiri | PillolaRitiri[] | null;
  kpi?: KpiRitiri[];
  /** Pulsanti accanto al titolo, es. "Importa" nella dashboard. */
  azioni?: ReactNode;
  /** Una riga sotto i KPI, es. da dove vengono i numeri. */
  notaKpi?: string;
}

function RiquadroKpi({ etichetta, valore, caldo }: KpiRitiri) {
  const animato = useCountUp(valore ?? 0);
  return (
    <div
      className={clsx(
        "rounded-[14px] border p-3",
        caldo ? "border-ritiri-caldo-chiaro/60 bg-ritiri-caldo/[.22]" : "border-white/[.14] bg-white/[.06]"
      )}
    >
      <div className="font-plex text-[30px] font-semibold leading-none text-white">
        {valore === null ? "—" : animato.toLocaleString("it-IT")}
      </div>
      <div className="mt-1.5 text-[11px] font-medium text-ritiri-testo-tenue">{etichetta}</div>
    </div>
  );
}

export function TestataRitiri({ sopratitolo, titolo, sottotitolo, pillola, kpi = [], notaKpi, azioni }: Props) {
  const pillole = pillola ? (Array.isArray(pillola) ? pillola : [pillola]) : [];
  return (
    <header className="relative -mx-4 -mt-6 overflow-hidden bg-primary px-4 pb-[26px] pt-[30px] text-white sm:-mx-6 sm:-mt-8 sm:px-10">
      <div className="ritiri-alone" aria-hidden="true" />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="ritiri-rise min-w-0">
          <p className="text-xs font-bold uppercase tracking-[1.6px] text-accent">{sopratitolo}</p>
          <h1 className="mt-2 text-[28px] font-extrabold leading-tight tracking-[-0.4px] sm:text-[34px]">{titolo}</h1>
          {sottotitolo && (
            <p className="mt-2 max-w-[760px] text-sm leading-[1.55] text-ritiri-testo-tenue">{sottotitolo}</p>
          )}
        </div>
        {azioni && <div className="ritiri-rise flex shrink-0 flex-wrap gap-2">{azioni}</div>}
      </div>

      {(pillole.length > 0 || kpi.length > 0) && (
        <div className="ritiri-fade relative mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {pillole.map((p, i) => (
              <span
                key={i}
                className={clsx(
                  "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[13px] font-semibold text-ritiri-pillola",
                  p.allarme ? "border-danger bg-danger/20" : "border-accent/[.45] bg-accent/[.14]"
                )}
              >
                <span
                  className={clsx("ritiri-blink h-2 w-2 shrink-0 rounded-full", p.allarme ? "bg-danger" : "bg-accent")}
                  aria-hidden="true"
                />
                {p.testo}
              </span>
            ))}
          </div>
          {kpi.length > 0 && (
            <div>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
                {kpi.map((k) => (
                  <RiquadroKpi key={k.etichetta} {...k} />
                ))}
              </div>
              {notaKpi && <p className="mt-2 text-right text-[11px] text-ritiri-testo-tenue">{notaKpi}</p>}
            </div>
          )}
        </div>
      )}
    </header>
  );
}

/**
 * Barra bianca con le schede sotto la testata. Con `onSeleziona` le schede
 * sono pulsanti (la dashboard ne ha due); senza, sono solo un'intestazione.
 */
export function SchedeRitiri({
  schede,
  onSeleziona,
}: {
  schede: { etichetta: string; attiva: boolean }[];
  onSeleziona?: (etichetta: string) => void;
}) {
  return (
    <nav
      aria-label="Sezioni Ritiri valori"
      className="-mx-4 border-b border-ritiri-bordo bg-white px-4 sm:-mx-6 sm:px-10"
    >
      <ul className="flex gap-6">
        {schede.map((s) => (
          <li key={s.etichetta}>
            {onSeleziona ? (
              <button
                type="button"
                aria-current={s.attiva ? "page" : undefined}
                onClick={() => onSeleziona(s.etichetta)}
                className={clsx(
                  "inline-block border-b-2 py-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  s.attiva ? "border-accent text-ink" : "border-transparent text-ritiri-th-testo hover:text-ink"
                )}
              >
                {s.etichetta}
              </button>
            ) : (
              <span
                aria-current={s.attiva ? "page" : undefined}
                className={clsx(
                  "inline-block border-b-2 py-3 text-sm font-semibold",
                  s.attiva ? "border-accent text-ink" : "border-transparent text-ritiri-th-testo"
                )}
              >
                {s.etichetta}
              </span>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Barra di avanzamento 8px con riempimento animato. */
export function BarraRitiri({ valore, massimo, tono = "primary" }: { valore: number; massimo: number; tono?: "primary" | "accent" | "caldo" | "neutro" }) {
  const quota = massimo > 0 ? Math.min(1, Math.max(0, valore / massimo)) : 0;
  const colore = { primary: "bg-primary", accent: "bg-accent", caldo: "bg-ritiri-caldo", neutro: "bg-ritiri-th-testo" }[tono];
  return (
    // Span e non div: la barra compare anche dentro i pulsanti dell'elenco della dashboard.
    <span className="block h-2 w-full overflow-hidden rounded-full bg-ritiri-traccia" aria-hidden="true">
      <span className={clsx("ritiri-barra block h-full rounded-full", colore)} style={{ width: `${quota * 100}%` }} />
    </span>
  );
}

/** Card bianca del modulo. */
export function CardRitiri({ titolo, azioni, children, className }: { titolo?: ReactNode; azioni?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={clsx("rounded-2xl border border-ritiri-bordo bg-white p-5 shadow-[0_1px_2px_rgba(24,39,61,.05)] sm:p-6", className)}>
      {(titolo || azioni) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {titolo && <h2 className="text-lg font-bold text-ink">{titolo}</h2>}
          {azioni}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Pulsante del modulo: principale (primary, testo bianco) o secondario
 * (bianco con bordo), altezza 44px, sollevamento al passaggio.
 */
export function PulsanteRitiri({
  variante = "principale",
  inCorso = false,
  disabled,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: "principale" | "secondario"; inCorso?: boolean }) {
  return (
    <button
      {...props}
      disabled={disabled || inCorso}
      aria-busy={inCorso || undefined}
      className={clsx(
        "ritiri-lift inline-flex h-11 items-center justify-center gap-2 rounded-[11px] px-5 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50",
        variante === "principale"
          ? "bg-primary text-white hover:bg-primary-light"
          : "border border-ritiri-bordo-forte bg-white text-ink hover:bg-ritiri-hover",
        className
      )}
    >
      {inCorso && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
      {children}
    </button>
  );
}
